import { useLanguage } from "@/components/language-provider";
import type { Lang } from "./fields";

/** UI strings of the CRM module: [pt, en]. Field/option labels live in fields.ts. */
const TEXT = {
  loading: ["Carregando…", "Loading…"],
  new: ["Novo", "New"],
  refresh: ["Atualizar", "Refresh"],
  save: ["Salvar", "Save"],
  saveClose: ["Salvar e fechar", "Save & close"],
  cancel: ["Cancelar", "Cancel"],
  back: ["Voltar", "Back"],
  delete: ["Excluir", "Delete"],
  activate: ["Ativar", "Activate"],
  deactivate: ["Desativar", "Deactivate"],
  assign: ["Atribuir", "Assign"],
  sendDocument: ["Enviar documento", "Send document"],
  more: ["Mais opções", "More options"],
  error: ["Erro", "Error"],
  none: ["—", "—"],
  select: ["--Selecione--", "--Select--"],
  date: ["Data", "Date"],
  time: ["Hora", "Time"],
  open: ["Abrir", "Open"],
  close: ["Fechar", "Close"],
  yes: ["Sim", "Yes"],
  no: ["Não", "No"],

  // Grid
  searchView: ["Pesquisar nesta exibição", "Search this view"],
  records: ["{n} registro(s)", "{n} record(s)"],
  selected: ["{n} selecionado(s)", "{n} selected"],
  noRecords: ["Nenhum registro nesta exibição.", "No records in this view."],
  page: ["Página {p} de {t}", "Page {p} of {t}"],
  prev: ["Anterior", "Previous"],
  next: ["Próxima", "Next"],
  selectAll: ["Selecionar todos", "Select all"],
  selectRow: ["Selecionar linha", "Select row"],
  sortAsc: ["Classificar A → Z", "Sort A → Z"],
  sortDesc: ["Classificar Z → A", "Sort Z → A"],
  deletedMany: ["{n} registro(s) excluído(s)", "{n} record(s) deleted"],
  activatedMany: ["{n} registro(s) ativado(s)", "{n} record(s) activated"],
  deactivatedMany: ["{n} registro(s) desativado(s)", "{n} record(s) deactivated"],
  assignedMany: ["{n} registro(s) atribuído(s)", "{n} record(s) assigned"],
  partialFail: ["{n} registro(s) não puderam ser alterados", "{n} record(s) could not be changed"],
  confirmDeleteTitle: ["Excluir registro?", "Delete record?"],
  confirmDeleteOne: ["Excluir este registro? A linha do tempo, os vínculos e o histórico dele também serão removidos. Esta ação não pode ser desfeita.", "Delete this record? Its timeline, links and history are removed too. This cannot be undone."],
  confirmDeleteMany: ["Excluir {n} registro(s)? A linha do tempo, os vínculos e o histórico também serão removidos. Esta ação não pode ser desfeita.", "Delete {n} record(s)? Timeline, links and history are removed too. This cannot be undone."],
  assignTitle: ["Atribuir {n} registro(s)", "Assign {n} record(s)"],
  newOwner: ["Novo proprietário", "New owner"],

  // Views
  views: ["Exibições", "Views"],
  systemViews: ["Exibições do sistema", "System views"],
  sharedViews: ["Exibições compartilhadas", "Shared views"],
  myViews: ["Minhas exibições", "My views"],
  editView: ["Editar colunas e filtros", "Edit columns and filters"],
  saveView: ["Salvar alterações na exibição", "Save changes to view"],
  saveAsView: ["Salvar como nova exibição", "Save as new view"],
  createView: ["Criar exibição", "Create view"],
  resetView: ["Restaurar exibição padrão", "Reset to default"],
  deleteView: ["Excluir exibição", "Delete view"],
  confirmDeleteView: ["Excluir a exibição \"{name}\"?", "Delete the view \"{name}\"?"],
  viewName: ["Nome da exibição", "View name"],
  shareView: ["Compartilhar com a empresa", "Share with the company"],
  shareViewHint: ["Todos da empresa veem esta exibição. Só você (e administradores) podem alterá-la.", "Everyone in the company sees this view. Only you (and admins) can change it."],
  systemViewHint: ["Exibição do sistema: as alterações valem para toda a empresa e podem ser desfeitas em \"Restaurar exibição padrão\".", "System view: changes apply to the whole company and can be undone with \"Reset to default\"."],
  columns: ["Colunas", "Columns"],
  addColumn: ["Adicionar coluna", "Add column"],
  filters: ["Filtros", "Filters"],
  stateFilter: ["Situação", "Status"],
  stateActiveContact: ["Ativos", "Active"],
  stateInactiveContact: ["Inativos", "Inactive"],
  stateActiveLead: ["Abertos", "Open"],
  stateInactiveLead: ["Encerrados", "Closed"],
  stateAll: ["Todos", "All"],
  ownerFilter: ["Proprietário", "Owner"],
  ownerMe: ["Meus registros", "My records"],
  ownerAny: ["Qualquer proprietário", "Any owner"],
  conditions: ["Condições (todas devem ser atendidas)", "Conditions (all must match)"],
  addCondition: ["Adicionar condição", "Add condition"],
  sortBy: ["Ordenar por", "Sort by"],
  direction: ["Direção", "Direction"],
  asc: ["Crescente", "Ascending"],
  desc: ["Decrescente", "Descending"],
  value: ["Valor", "Value"],
  op_eq: ["é igual a", "equals"],
  op_neq: ["é diferente de", "does not equal"],
  op_contains: ["contém", "contains"],
  op_notcontains: ["não contém", "does not contain"],
  op_empty: ["está vazio", "is empty"],
  op_notempty: ["contém dados", "has data"],
  op_gt: ["é maior que", "is greater than"],
  op_lt: ["é menor que", "is less than"],
  viewSaved: ["Exibição salva", "View saved"],
  viewDeleted: ["Exibição excluída", "View deleted"],
  viewReset: ["Exibição restaurada", "View reset"],
  sharedBy: ["Compartilhada por {name}", "Shared by {name}"],
  sharedTag: ["compartilhada", "shared"],
  customizedTag: ["personalizada", "customized"],
  viewChanged: ["Exibição alterada", "View changed"],
  copyOf: ["{name} (cópia)", "{name} (copy)"],
  needColumn: ["A exibição precisa de pelo menos uma coluna.", "A view needs at least one column."],
  needName: ["Dê um nome à exibição.", "Give the view a name."],

  // Record
  newContact: ["Novo cliente", "New contact"],
  newLead: ["Novo lead", "New lead"],
  summary: ["Resumo", "Summary"],
  files: ["Arquivos", "Files"],
  auditHistory: ["Histórico de auditoria", "Audit history"],
  saved: ["Registro salvo", "Record saved"],
  created: ["Registro criado", "Record created"],
  deleted: ["Registro excluído", "Record deleted"],
  activated: ["Registro ativado", "Record activated"],
  deactivated: ["Registro desativado", "Record deactivated"],
  inactiveContact: ["Este cliente está inativo (somente leitura). Ative-o para editar.", "This contact is inactive (read-only). Activate it to edit."],
  disqualifiedLead: ["Lead desqualificado (somente leitura). Reabra para editar.", "Lead disqualified (read-only). Reopen it to edit."],
  closedLead: ["Lead encerrado (somente leitura). Reabra para editar.", "Lead closed (read-only). Reopen it to edit."],
  acceptedLead: ["Lead aceito por vendas em {date} (somente leitura).", "Lead accepted by sales on {date} (read-only)."],
  openContact: ["Abrir cliente", "Open contact"],
  reopen: ["Reabrir lead", "Reopen lead"],
  reopened: ["Lead reaberto", "Lead reopened"],
  disqualify: ["Desqualificar", "Disqualify"],
  disqualified: ["Lead desqualificado", "Lead disqualified"],
  confirmDisqualify: ["Desqualificar este lead? Ele será encerrado com status \"Não qualificado\".", "Disqualify this lead? It is closed as \"Not qualified\"."],
  customizeForm: ["Personalizar formulário", "Customize form"],
  saveFirst: ["Salve o registro para usar a linha do tempo.", "Save the record to use the timeline."],
  fixErrors: ["Corrija os campos destacados", "Fix the highlighted fields"],
  err_required: ["Campo obrigatório", "Required"],
  err_invalid: ["Valor inválido", "Invalid value"],
  err_maxLength: ["Texto muito longo", "Too long"],
  err_invalidOption: ["Opção inválida", "Invalid option"],
  err_phone: ["Telefone inválido: DDD + número, ou +código do país", "Invalid phone: area code + number, or +country code"],
  err_email: ["E-mail inválido", "Invalid e-mail"],
  err_url: ["Endereço inválido (ex.: https://site.com.br)", "Invalid address (e.g. https://site.com)"],
  err_number: ["Informe um número inteiro", "Enter a whole number"],
  err_oneEmail: ["Informe o e-mail pessoal ou o corporativo", "Enter the personal or the work email"],
  err_sameEmail: ["Os dois e-mails são iguais", "Both emails are the same"],
  err_cpf: ["CPF inválido", "Invalid CPF"],
  err_cnpj: ["CNPJ inválido", "Invalid CNPJ"],
  duplicateEmail: ["Já existe um cliente com este e-mail: {name}.", "A contact with this e-mail already exists: {name}."],
  notFound: ["Registro não encontrado ou sem acesso.", "Record not found or no access."],
  unsavedTitle: ["Descartar alterações?", "Discard changes?"],
  unsavedText: ["Há alterações não salvas neste registro.", "This record has unsaved changes."],
  discard: ["Descartar", "Discard"],
  keepEditing: ["Continuar editando", "Keep editing"],
  unsaved: ["Não salvo", "Unsaved"],
  callPhone: ["Ligar", "Call"],
  whatsapp: ["WhatsApp", "WhatsApp"],
  writeEmail: ["Escrever e-mail", "Write e-mail"],
  defaultEmail: ["Padrão", "Default"],
  makeDefault: ["Tornar padrão", "Make default"],
  defaultEmailHint: ["E-mail usado por padrão ao enviar documentos", "Email used by default when sending documents"],
  addPhoto: ["Adicionar foto", "Add photo"],
  changePhoto: ["Trocar foto", "Change photo"],
  removePhoto: ["Remover foto", "Remove photo"],
  photoSaved: ["Foto atualizada", "Photo updated"],
  photoRemoved: ["Foto removida", "Photo removed"],
  photoInvalid: ["Não foi possível usar esta imagem. Escolha um arquivo JPG, PNG ou WebP.", "This image cannot be used. Pick a JPG, PNG or WebP file."],
  contactHasLeads: ["Este cliente tem {n} lead(s) vinculado(s). Exclua os leads ou vincule-os a outro cliente antes.", "This contact has {n} linked lead(s). Delete them or link them to another contact first."],
  leadNeedsClient: ["Este lead ainda não tem cliente. Escolha um cliente cadastrado (ou cadastre um) no campo Cliente para salvar as alterações.", "This lead has no contact yet. Pick a registered contact (or register one) in the Contact field to save changes."],
  clientSearch: ["Pesquise o cliente pelo nome", "Search the contact by name"],
  clientCreate: ["Cadastrar novo cliente", "Register a new contact"],
  clientCreateNamed: ["Cadastrar “{name}” como novo cliente", "Register “{name}” as a new contact"],
  clientHint: ["Nome, e-mail e telefones do lead vêm do cadastro do cliente.", "The lead's name, email and phones come from the contact record."],
  changeClient: ["Trocar", "Change"],
  newClientTitle: ["Novo cliente", "New contact"],
  newClientDesc: ["O cliente é cadastrado agora e já fica vinculado a este lead.", "The contact is registered now and linked to this lead."],
  createAndLink: ["Cadastrar e vincular", "Register and link"],
  clientCreated: ["Cliente cadastrado", "Contact registered"],
  useExistingClient: ["Vincular {name}", "Link {name}"],
  relatedLead: ["Lead: {name}", "Lead: {name}"],
  relatedClient: ["Cliente: {name}", "Contact: {name}"],
  relatedOpen: ["Abrir registro", "Open record"],

  // Lead process
  stageNext: ["Próxima etapa", "Next stage"],
  stageBack: ["Etapa anterior", "Previous stage"],
  finish: ["Concluir", "Finish"],
  acceptExplain: ["Ao concluir, o lead é encerrado como aceito por vendas. Atividades e documentos continuam na linha do tempo do cliente.", "On finish, the lead is closed as sales accepted. Its activities and documents stay on the contact's timeline."],
  acceptCreated: ["Lead aceito. Cliente criado.", "Lead accepted. Contact created."],
  acceptLinked: ["Lead aceito e vinculado ao cliente existente.", "Lead accepted and linked to the existing contact."],
  stageMissing: ["Para avançar, preencha: {fields}", "To move on, fill in: {fields}"],
  stageMoved: ["Etapa: {stage}", "Stage: {stage}"],
  activeFor: ["Etapa ativa", "Active stage"],
  processDone: ["Processo concluído", "Process finished"],
  processDisqualified: ["Processo encerrado: desqualificado", "Process ended: disqualified"],
  processClosed: ["Processo encerrado", "Process ended"],
  assignTo: ["Atribuir a", "Assign to"],
  assignUser: ["Usuário do FillSign", "FillSign user"],
  assignOther: ["Outra pessoa (texto livre)", "Someone else (free text)"],
  assignOtherPlaceholder: ["Nome do responsável", "Owner name"],
  leadProcess: ["Processo do lead", "Lead process"],

  // Timeline
  timeline: ["Linha do tempo", "Timeline"],
  searchTimeline: ["Pesquisar na linha do tempo", "Search timeline"],
  enterNote: ["Inserir uma nota…", "Enter a note…"],
  kind_note: ["Nota", "Note"],
  kind_task: ["Tarefa", "Task"],
  kind_appointment: ["Compromisso", "Appointment"],
  kind_call: ["Ligação", "Phone call"],
  kind_email: ["E-mail", "E-mail"],
  kind_signature: ["Assinatura", "Signature"],
  kind_signed: ["Assinado", "Signed"],
  kind_system: ["Sistema", "System"],
  new_note: ["Nova nota", "New note"],
  new_task: ["Nova tarefa", "New task"],
  new_appointment: ["Novo compromisso", "New appointment"],
  new_call: ["Registrar ligação", "Log a phone call"],
  new_email: ["Registrar e-mail", "Log an e-mail"],
  new_signature: ["Assinatura (enviar documento)", "Signature (send document)"],
  edit_activity: ["Editar atividade", "Edit activity"],
  subject: ["Assunto", "Subject"],
  title: ["Título", "Title"],
  description: ["Descrição", "Description"],
  noteText: ["Texto da nota", "Note text"],
  dueAt: ["Vencimento", "Due"],
  callAt: ["Data da ligação", "Call date"],
  startAt: ["Início", "Start"],
  endAt: ["Término", "End"],
  location: ["Local", "Location"],
  sortNewest: ["Mais recentes primeiro", "Newest first"],
  sortOldest: ["Mais antigos primeiro", "Oldest first"],
  show: ["Mostrar", "Show"],
  showActivities: ["Atividades", "Activities"],
  showDocuments: ["Documentos", "Documents"],
  showSystem: ["Eventos do sistema", "System events"],
  googleCalendar: ["Google Agenda", "Google Calendar"],
  outlookCalendar: ["Outlook", "Outlook"],
  downloadIcs: ["Baixar .ics", "Download .ics"],
  openDocument: ["Abrir documento", "Open document"],
  signedBy: ["Assinado por {name}", "Signed by {name}"],
  sentTo: ["Enviado para {names}", "Sent to {names}"],
  by: ["por {name}", "by {name}"],
  completedTag: ["Concluída", "Completed"],
  markDone: ["Marcar como concluída", "Mark as completed"],
  markOpen: ["Reabrir tarefa", "Reopen task"],
  overdue: ["Atrasada", "Overdue"],
  emptyTimeline: ["Nenhuma atividade ainda. Use + para registrar nota, tarefa, compromisso ou ligação.", "No activity yet. Use + to log a note, task, appointment or call."],
  noMatches: ["Nada encontrado.", "Nothing found."],
  sys_create: ["Registro criado", "Record created"],
  sys_activate: ["Registro ativado", "Record activated"],
  sys_deactivate: ["Registro desativado", "Record deactivated"],
  sys_stage: ["Etapa alterada: {from} → {to}", "Stage changed: {from} → {to}"],
  sys_accept: ["Lead aceito por vendas", "Lead accepted by sales"],
  sys_disqualify: ["Lead desqualificado", "Lead disqualified"],
  sys_update: ["Registro atualizado", "Record updated"],
  sys_fromLead: ["Criado a partir de um lead", "Created from a lead"],
  activitySaved: ["Atividade salva", "Activity saved"],
  activityDeleted: ["Atividade excluída", "Activity deleted"],
  confirmDeleteActivity: ["Excluir esta atividade?", "Delete this activity?"],
  openMail: ["Abrir no e-mail", "Open in mail"],
  emailLogged: ["E-mail registrado na linha do tempo", "E-mail logged on the timeline"],

  // Audit
  changedOn: ["Data da alteração", "Changed on"],
  changedBy: ["Alterado por", "Changed by"],
  event: ["Evento", "Event"],
  changedField: ["Campo alterado", "Changed field"],
  oldValue: ["Valor antigo", "Old value"],
  newValue: ["Novo valor", "New value"],
  allFields: ["Todos os campos", "All fields"],
  noAudit: ["Nenhuma alteração registrada.", "No changes recorded."],
  op_create: ["Criar", "Create"],
  op_update: ["Atualizar", "Update"],
  op_activate: ["Ativar", "Activate"],
  op_deactivate: ["Desativar", "Deactivate"],
  op_stage: ["Mudança de etapa", "Stage change"],
  op_accept: ["Aceite de vendas", "Sales accept"],
  op_disqualify: ["Desqualificar", "Disqualify"],
  leadIdField: ["Lead de origem", "Source lead"],

  // Files
  document: ["Documento", "Document"],
  status: ["Status", "Status"],
  sentBy: ["Enviado por", "Sent by"],
  signers: ["Assinantes", "Signers"],
  sentOn: ["Enviado em", "Sent on"],
  source: ["Origem", "Source"],
  linkedSource: ["Enviado deste registro", "Sent from this record"],
  emailSource: ["Pelo e-mail do registro", "By the record's e-mail"],
  viaLead: ["Enviado do lead “{name}”", "Sent from lead “{name}”"],
  viaClient: ["Enviado do cliente {name}", "Sent from contact {name}"],
  noFiles: ["Nenhum documento relacionado. Envie um documento para assinatura a partir deste registro.", "No related documents. Send a document for signature from this record."],

  // Form designer
  designerTitle: ["Personalizar formulário: {entity}", "Customize form: {entity}"],
  designerHint: ["Arraste os campos ou use as setas para reordenar e mover entre seções. Marque \"Obrigatório\" para exigir o campo. Vale para toda a empresa.", "Drag fields or use the arrows to reorder and move them between sections. Tick \"Required\" to make a field mandatory. Applies to the whole company."],
  addSection: ["Adicionar seção", "Add section"],
  sectionName: ["Nome da seção", "Section name"],
  removeSection: ["Remover seção", "Remove section"],
  moveUp: ["Mover para cima", "Move up"],
  moveDown: ["Mover para baixo", "Move down"],
  moveTo: ["Mover para…", "Move to…"],
  required: ["Obrigatório", "Required"],
  removeField: ["Remover do formulário", "Remove from form"],
  availableFields: ["Campos disponíveis", "Available fields"],
  allPlaced: ["Todos os campos já estão no formulário.", "All fields are on the form."],
  addField: ["Adicionar campo…", "Add field…"],
  resetDefault: ["Restaurar padrão", "Reset to default"],
  formSaved: ["Formulário salvo", "Form saved"],
  formReset: ["Formulário restaurado ao padrão", "Form reset to default"],
  lockedField: ["Campo essencial: não pode sair do formulário", "Essential field: cannot leave the form"],
  emptySection: ["Arraste campos para cá", "Drop fields here"],
  newSection: ["Nova seção", "New section"],
  systemSectionNote: ["A seção \"Informações do sistema\" e a linha do tempo são fixas.", "The \"System information\" section and the timeline are fixed."],
  mustKeep: ["Estes campos precisam ficar no formulário: {fields}", "These fields must stay on the form: {fields}"],
  maxSections: ["Limite de 12 seções", "Up to 12 sections"],

  // Address
  validateCep: ["Validar CEP", "Validate ZIP"],
  validateAddress: ["Validar endereço", "Validate address"],
  validating: ["Validando…", "Validating…"],
  cepInvalid: ["CEP inválido: informe 8 dígitos", "Invalid ZIP: enter 8 digits"],
  cepNotFound: ["CEP não encontrado", "ZIP not found"],
  cepFilled: ["CEP encontrado: endereço preenchido", "ZIP found: address filled in"],
  addressFound: ["Endereço localizado no mapa", "Address found on the map"],
  addressNotFound: ["Endereço não localizado no mapa. Confira logradouro e número.", "Address not found on the map. Check street and number."],
  addressIncomplete: ["Endereço incompleto. Informe: {fields}", "Incomplete address. Fill in: {fields}"],
  addressValidated: ["Endereço validado", "Address validated"],
  addressChanged: ["Endereço alterado: valide novamente", "Address changed: validate again"],
  approxLocation: ["Localização aproximada (CEP/cidade)", "Approximate location (ZIP/city)"],
  openGoogleMaps: ["Abrir no Google Maps", "Open in Google Maps"],
  openOsm: ["Ver no OpenStreetMap", "View on OpenStreetMap"],
  lookupFailed: ["Não foi possível consultar o CEP agora. Tente novamente.", "ZIP lookup is unavailable right now. Try again."],
  mapTitle: ["Mapa do endereço", "Address map"],

  // Module state
  noCompany: ["O módulo CRM está disponível para contas com empresa. Crie sua empresa em Configurações.", "The CRM module is available for company accounts. Create your company in Settings."],
  disabledTitle: ["Módulo CRM (Clientes e Leads) desativado", "CRM module (Contacts and Leads) is off"],
  disabledDesc: ["O CRM da empresa (clientes, leads, exibições, linha do tempo, arquivos e histórico de auditoria) está desligado.", "The company CRM (contacts, leads, views, timeline, files and audit history) is turned off."],
  openSettings: ["Abrir Configurações", "Open Settings"],
  enableInSettings: ["O módulo é ligado em Configurações → Módulo CRM (só administradores da empresa).", "The module is turned on in Settings → CRM module (company admins only)."],
  askAdmin: ["Peça a um administrador da empresa para ativar o módulo.", "Ask a company admin to enable the module."],
  crmEnabled: ["CRM ativado", "CRM enabled"],
  crmDisabled: ["CRM desativado", "CRM disabled"],
  disableModule: ["Desativar CRM da empresa", "Disable CRM for the company"],
  confirmDisableModule: ["Desativar o CRM para toda a empresa? Os dados ficam guardados e voltam ao reativar.", "Disable the CRM for the whole company? Data is kept and comes back when re-enabled."],
  notAllowed: ["Sem permissão", "Not allowed"],
  adminOnly: ["Apenas administradores da empresa podem fazer isso.", "Only company admins can do this."],
} as const;

export type CrmTextKey = keyof typeof TEXT;

export function crmText(lang: Lang, key: CrmTextKey, vars?: Record<string, string | number>): string {
  const pair = TEXT[key];
  let s: string = pair ? pair[lang === "pt" ? 0 : 1] : key;
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

/** Language + CRM text helper for components. */
export function useCrmText() {
  const { language } = useLanguage();
  const lang: Lang = language === "pt" ? "pt" : "en";
  return {
    lang,
    pt: lang === "pt",
    tx: (key: CrmTextKey, vars?: Record<string, string | number>) => crmText(lang, key, vars),
  };
}
